import { type FC, useCallback } from "react";
import { useFormContext, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";
import type { EmailDesignSchema } from "../../email-design/types/email-design-type";
import { CeColourField } from "../../generic/components/form-fields/ce-colour-field";
import { CeRadio } from "../../generic/components/form-fields/ce-radio";
import { CeSlider } from "../../generic/components/form-fields/ce-slider";
import { CeToggle } from "../../generic/components/form-fields/ce-toggle";
import { FormStack } from "../../generic/components/form-fields/form-stack";
import { useStyleableColumns } from "../../message/editor/utils/use-styleable-columns";

export const LayoutEditor: FC = () => {
  const { t } = useTranslation();
  const stylableColumns = useStyleableColumns();
  const { control, getValues, setValue } = useFormContext<EmailDesignSchema>();

  useWatch({
    control,
    name: ["layout_settings.column_spacing_type", "layout_settings.column_individual_padding"],
  });
  const [spacingType, individualPaddingType] = getValues([
    "layout_settings.column_spacing_type",
    "layout_settings.column_individual_padding",
  ]);

  const handleToggleIndividualPaddings = useCallback(
    (checked: boolean): void => setValue("layout_settings.column_individual_padding", checked),
    [setValue],
  );
  return (
    <FormStack>
      <CeColourField<EmailDesignSchema>
        analyticsCategory="email_design"
        name="layout_settings.background_colour"
        label={t("Background Color")}
      />
      <CeColourField<EmailDesignSchema>
        analyticsCategory="email_design"
        name="layout_settings.paper_colour"
        label={t("Paper Color")}
      />
      <CeSlider<EmailDesignSchema>
        analyticsCategory="email_design"
        name="layout_settings.max_paper_width"
        label={t("Maximum Paper Width")}
        min={500}
        max={1000}
        step={50}
      />
      <CeSlider<EmailDesignSchema>
        analyticsCategory="email_design"
        name="layout_settings.paper_padding_side"
        label={t("Side Padding")}
        min={0}
        max={50}
        step={1}
      />
      <CeSlider<EmailDesignSchema>
        analyticsCategory="email_design"
        name="layout_settings.paper_padding_top"
        label={t("Top Padding")}
        min={0}
        max={50}
        step={1}
      />
      <CeSlider<EmailDesignSchema>
        analyticsCategory="email_design"
        name="layout_settings.paper_padding_bottom"
        label={t("Bottom Padding")}
        min={0}
        max={50}
        step={1}
      />
      <CeSlider<EmailDesignSchema>
        analyticsCategory="email_design"
        name="layout_settings.paragraph_padding"
        label={t("Paragraph Padding")}
      />
      <CeColourField<EmailDesignSchema>
        analyticsCategory="email_design"
        name="layout_settings.border.colour"
        label={t("Border Color")}
      />
      <CeSlider<EmailDesignSchema>
        analyticsCategory="email_design"
        name="layout_settings.border.width"
        label={t("Border Width")}
        min={0}
        max={10}
        step={1}
      />
      {!stylableColumns.loading && !stylableColumns.enabled && (
        <CeRadio<EmailDesignSchema>
          analyticsCategory="email_design"
          label={t("Column Spacing")}
          name="layout_settings.column_spacing_type"
          options={[
            { value: "spacing", label: "Outside" },
            { value: "padding", label: "Inside" },
            { value: "none", label: "None" },
          ]}
        />
      )}
      {!stylableColumns.loading && !stylableColumns.enabled && spacingType === "spacing" && (
        <CeSlider<EmailDesignSchema>
          analyticsCategory="email_design"
          formControlStyle={{ marginTop: 0 }}
          name="layout_settings.column_spacing"
          max={50}
          step={1}
        />
      )}
      {!stylableColumns.loading && !stylableColumns.enabled && spacingType === "padding" && (
        <CeSlider<EmailDesignSchema>
          analyticsCategory="email_design"
          formControlStyle={{ marginTop: 0 }}
          name="layout_settings.column_padding"
          max={50}
          step={1}
        />
      )}
      {stylableColumns.enabled && (
        <>
          <CeToggle
            labelPlacement="top"
            name="layout_settings.column_individual_padding"
            label="Column Padding Individual Values"
            onChange={handleToggleIndividualPaddings}
          />

          {!individualPaddingType && (
            <CeSlider<EmailDesignSchema>
              analyticsCategory="email_design"
              formControlStyle={{ marginTop: 0 }}
              name="layout_settings.column_padding"
              max={50}
              step={1}
              label={t("Column Padding")}
            />
          )}

          {individualPaddingType && (
            <>
              <CeSlider<EmailDesignSchema>
                analyticsCategory="email_design"
                formControlStyle={{ marginTop: 0 }}
                name="layout_settings.column_padding_top"
                max={50}
                step={1}
                label={t("Column Padding Top")}
              />
              <CeSlider<EmailDesignSchema>
                analyticsCategory="email_design"
                formControlStyle={{ marginTop: 0 }}
                name="layout_settings.column_padding_bottom"
                max={50}
                step={1}
                label={t("Column Padding Bottom")}
              />
              <CeSlider<EmailDesignSchema>
                analyticsCategory="email_design"
                formControlStyle={{ marginTop: 0 }}
                name="layout_settings.column_padding_left"
                max={50}
                step={1}
                label={t("Column Padding Left")}
              />
              <CeSlider<EmailDesignSchema>
                analyticsCategory="email_design"
                formControlStyle={{ marginTop: 0 }}
                name="layout_settings.column_padding_right"
                max={50}
                step={1}
                label={t("Column Padding Right")}
              />
            </>
          )}
        </>
      )}
    </FormStack>
  );
};
